/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import HomeTagImg from '@assets/images/icon/matchup_icon/home-tag-ico.png'
import { CompanyPickDetailContentsWrap } from '@views/Popup/CompanyPickDetail/index.style'
import { MATCH_GAME_STATUS, MATCH_STATUS } from 'adv-protocol/src/GameCommon'
import {
  isCanceledMatchStatus,
  isClosedMatchStatus,
  isConsideredOngoingMatchStatus,
} from '@utils/utils'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'

dayjs.locale('ko')

export interface IMatchInfo {
  dateOfMatch: Date
  sportId: number
  homeTeamName: string
  homeTeamEmblem: string
  homeTeamScore?: number
  awayTeamName: string
  awayTeamEmblem: string
  awayTeamScore?: number
  feedSupplier?: string
  marketType?: string
  outcomeRate?: string
  hitState?: number
  matchStatus?: MATCH_STATUS
  status?: MATCH_GAME_STATUS
}

export interface MatchInfoProps {
  matchInfo: IMatchInfo
  large?: boolean
  small?: boolean
  vertical?: boolean
  horizon?: boolean
}

export const UserPickMatchInfo: React.FC<MatchInfoProps> = observer((props) => {
  const { matchInfo, large = false, small = true, vertical = false } = props
  const {
    dateOfMatch,
    sportId,
    homeTeamName,
    homeTeamEmblem,
    awayTeamName,
    awayTeamEmblem,
    homeTeamScore = 0,
    awayTeamScore = 0,
    matchStatus,
  } = matchInfo

  const date = dayjs(dateOfMatch).format('MM/DD(ddd)')
  const time = dayjs(dateOfMatch).format('HH:mm')

  const homeScoreClassName = homeTeamScore > awayTeamScore ? 'win' : ''
  const awayScoreClassName = homeTeamScore < awayTeamScore ? 'win' : ''

  let matchStatusTag: React.ReactElement
  let isClosed = false
  let isCanceled = false
  if (isClosedMatchStatus(matchStatus)) {
    matchStatusTag = <span className="end-tag">경기종료</span>
    isClosed = true
    isCanceled = false
  } else if (isCanceledMatchStatus(matchStatus)) {
    matchStatusTag = <span className="cancel-tag">경기취소</span>
    isClosed = false
    isCanceled = true
  } else if (isConsideredOngoingMatchStatus(matchStatus, dateOfMatch)) {
    matchStatusTag = <span className="live-tag">진행중</span>
    isClosed = false
    isCanceled = false
  }

  let contents: React.ReactElement
  if (large) {
    contents = (
      <CompanyPickDetailContentsWrap>
        <div className="team-info-box large">
          <span className="team-info-box--item">
            <img src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="홈팀 엠블럼" className="emblem" loading="lazy"/>
            <span className="team">{homeTeamName}</span>
            <img src={HomeTagImg} alt="홈팀" className="home-tag-img" loading="lazy"/>
          </span>
          <span className="team-info-box--item">
            <div className="vs-bg">VS</div>
            <span className="date">{date}</span>
            <span>{time}</span>
          </span>
          <span className="team-info-box--item">
            <img src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="원정팀 엠블럼" className="emblem" loading="lazy"/>
            <span className="team">{awayTeamName}</span>
          </span>
        </div>
      </CompanyPickDetailContentsWrap>
    )
  } else if (small && vertical) {
    contents = (
      <div className="match-info">
        <div className="top-info">
          <span className="date">
            {`${date} ${time}`}
          </span>
          <div className="select-info">
            {matchStatusTag}
          </div>
        </div>

        <div className="score-wrapper">
          <div className="team home-team">
            <span className="emblem">
              <img src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="홈팀 엠블럼" loading="lazy"/>
            </span>
            {homeTeamName}
          </div>
          {isClosed && <span className={`score ${homeScoreClassName}`}>{homeTeamScore}</span>}
          {isCanceled && <span className="score">-</span>}
        </div>
        <div className="score-wrapper">
          <div className="team">
            <span className="emblem">
              <img src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="원정팀 엠블럼" loading="lazy"/>
            </span>
            {awayTeamName}
          </div>
          {isClosed && <span className={`score ${awayScoreClassName}`}>{awayTeamScore}</span>}
          {isCanceled && <span className="score">-</span>}
        </div>
      </div>
    )
  }

  return contents
})
